import {
  ab,
  ad,
  ao,
  apo,
  aroon,
  atr,
  bb,
  bop,
  dema,
  rsi,
  sma,
  stoch,
  cmf,
  cfo,
  cci,
  dc,
  ema,
  kc,
  macd,
  mfi,
  nvi,
  mi,
  obv,
  psar,
  ppo,
  pvo,
  qstick,
  trima,
  tema,
  trueRange,
  typprice,
  vwap,
  vwma,
  vortex,
  willr,
} from "indicatorts";
import { CandlestickData, Time } from "lightweight-charts";
import {
  earnings,
  eri,
  getGrideValues,
  ichimoku,
  rvgi,
} from "./customIndicators";
import { generatePayloadNew } from "@/lib/utils";
import {
  LineSeries,
  AreaSeries,
  LineStyle,
  HistogramSeries,
} from "lightweight-charts-v5";
import { HLCAreaSeries } from "./lightweight-charts/plugins/hlc-area-series/hlc-area-series";
import { Earningsseries } from "./lightweight-charts/plugins/earning-series/earnings-series";

interface CandlestickWithVolume extends CandlestickData<Time> {
  value: number;
}
interface AddIndicatorProps {
  indicator: any;
  tickerData: CandlestickWithVolume[];
  indicatorData?: any;
  chartRef: any;
  lineSeriesRef?: any;
  bottomChartRef?: any;
  mainChartRef?: any;
  postCommonIndicators?: any;
  currentPrice?: number;
  gridData?: any;
  candleStickSeriesRef?: any;
  earningsData?: any;
}

interface IndicatorDataPoint {
  time: number;
  value: any;
}

interface IndicatorResult {
  result: IndicatorDataPoint[];
  containsInnerObject: boolean;
}
export const addIndicatorToChart = async ({
  indicator,
  tickerData,
  lineSeriesRef,
  chartRef,
  postCommonIndicators,
  gridData,
  candleStickSeriesRef,
  currentPrice,
  earningsData,
}: AddIndicatorProps) => {
  const priceValues = tickerData.map((item) => item.open);
  const closePrices = tickerData.map((item) => item.close);
  const highPrices = tickerData.map((item) => item.high);
  const lowPrices = tickerData.map((item) => item.low);
  const volumePrices = tickerData.map((item) => item.volume);

  const indicatorClose = tickerData.map((item: any) => item.close);
  const indicatorHigh = tickerData.map((item: any) => item.high);
  const indicatorLow = tickerData.map((item: any) => item.low);
  const indicatorOpen = tickerData.map((item: any) => item.open);
  const indicatorVolume = tickerData.map((item: any) => item.volume);

  const times = tickerData.map((item) => item.time);
  const indicatorTimes = tickerData.map((item: any) => item.time);

  const config = prepareIndicatorConfig(indicator);
  const generateLineData = (values: number[]) =>
    values
      ?.map((value, idx) => ({ time: times[idx], value }))
      .filter(
        (i) =>
          i.time && typeof i.value === "number" && Number.isFinite(i.value),
      );
  const generateFullLineData = (values: number[]) =>
    values
      ?.map((value, idx) => ({ time: indicatorTimes[idx], value }))
      .filter((i) => i.time);
  const generateSeries = (
    chart: any,
    data: any,
    title?: string,
    plane?: number,
    index: number = 0,
    type: "area" | "line" | "histogram" = "line",
  ) => {
    try {
      if (plane === -1) return;
      data = filterValidAreaSeriesData(data);

      const seriesOptions = {
        color: getRandomColor(),
        lineWidth: 2,
        priceLineVisible: true,
        ...(title
          ? { title }
          : {
              title: `${indicator?.short_name?.toUpperCase()} ${
                config?.period ? " Period " + config.period : ""
              }`,
            }),
      };

      let series;
      if (type == "line") {
        series = chart.addSeries(LineSeries, seriesOptions, plane);
      } else if (type == "histogram") {
        series = chart.addSeries(
          HistogramSeries,
          {
            color: "#26a69a",
            priceFormat: {
              type: "price",
            },
            ...(title
              ? { title }
              : {
                  title: `${indicator?.short_name?.toUpperCase()} ${
                    config?.period ? " Period " + config.period : ""
                  }`,
                }),
          },
          plane,
        );
      } else {
        series = chart.addSeries(AreaSeries, seriesOptions, plane);
      }
      series.setData(data);
      plane && chart.panes()?.[1]?.setStretchFactor(0.3);
      plane && chart.panes()?.[2]?.setStretchFactor(0.3);
      plane && chart.panes()?.[3]?.setStretchFactor(0.3);
      return series;
    } catch (error) {
      console.warn("error generating series", error);
    }
  };
  const getNextEmptyPaneIndex = () => {
    const panes = chartRef?.current?.panes?.() || [];

    // Start checking from pane index 1, since 0 is the default and assumed to always exist
    for (let i = 1; i <= 3; i++) {
      if (!panes[i]) {
        return i;
      }
    }

    // All 3 panes exist (0, 1, and 2), so return 3 for the next new one
    return -1;
  };
  const getCommonIndicatorData = async (
    indicator: any,
    config: any,
    raw: boolean = false,
  ): Promise<IndicatorResult | any> => {
    try {
      config = Object.fromEntries(
        Object.entries(config).map(([key, value]) => [
          key.replace(/ /g, "_"),
          value,
        ]),
      );

      const payload = generatePayloadNew(
        indicatorHigh,
        indicatorLow,
        indicatorClose,
        indicatorOpen,
        indicatorVolume,
        indicatorTimes,
        config,
      );

      const response = await postCommonIndicators({
        indicator: indicator?.short_name,
        formData: payload,
      });

      if (!response) {
        return { containsInnerObject: false, result: [] };
      }

      if (raw) {
        return response;
      }

      const firstKey = Object.keys(response || {})[0];
      const isColumnOriented = firstKey && isNaN(Number(firstKey));

      let resultArray: any[] = [];
      let containsInnerObject = false;

      const recentTimes = indicatorTimes.slice(-3000);

      if (isColumnOriented) {
        // Handle column-oriented response (e.g., Squeeze)
        const seriesNames = Object.keys(response);
        const firstSeriesIndices = Object.keys(response[firstKey]);

        resultArray = firstSeriesIndices.map((idx) => {
          const rowValue: any = {};
          seriesNames.forEach((name) => {
            rowValue[name] = response[name][idx];
          });
          return {
            time: recentTimes[Number(idx)],
            value: rowValue,
          };
        });
        containsInnerObject = true;
      } else {
        // Handle standard row-oriented or single-value response
        resultArray = Object.entries(response).map(([key, value]) => ({
          time: recentTimes?.[Number(key)],
          value: value ?? 0,
        }));

        containsInnerObject =
          typeof resultArray[0]?.value === "object" &&
          resultArray[0]?.value !== null &&
          Object.keys(resultArray[0].value).length > 0;
      }

      return {
        result: resultArray,
        containsInnerObject,
      };
    } catch (error) {
      console.error("Error in getCommonIndicatorData:", error);
      return { result: [], containsInnerObject: false };
    }
  };

  switch (indicator?.short_name) {
    case "apo": {
      const apoData = generateLineData(apo(closePrices, config));
      return [
        addOrUpdateLineSeries(
          lineSeriesRef,
          indicator,
          chartRef,
          config,
          apoData,
        ),
      ];
    }
    case "accbands": {
      // Bollinger Bands
      const { upper, middle, lower } = ab(
        highPrices,
        lowPrices,
        closePrices,
        config,
      );
      const result = upper.map((high, i) => ({
        open: null,
        low: lower[i],
        close: middle[i],
        high,
        time: times[i],
      }));
      if (lineSeriesRef?.length > 0) {
        lineSeriesRef[0].setData(result);
        lineSeriesRef[0].applyOptions({
          title: `A. Bands (Middle) - Period: ${config?.period}`,
        });
        return lineSeriesRef;
      }

      const customSeriesView = new HLCAreaSeries();
      if (lineSeriesRef?.length > 0) {
        lineSeriesRef[0].setData(result);
        return lineSeriesRef;
      }

      const lineSeries = chartRef?.current?.addCustomSeries(customSeriesView, {
        closeLineColor: getRandomColor(),
        color: getRandomColor(),
        priceLineVisible: false,
        title: `A. Bands (Middle) - Period: ${config?.period}`,
      });
      lineSeries.setData(result);

      return [lineSeries];
    }
    case "ad": {
      const adData = ad(
        indicatorHigh,
        indicatorLow,
        indicatorClose,
        indicatorVolume,
      )
        .map((value: any, idx: number) => ({
          time: indicatorTimes[idx],
          value,
        }))
        .filter((item: any) => !isNaN(item.value));

      return [
        generateSeries(
          chartRef?.current,
          adData,
          undefined,
          getNextEmptyPaneIndex(),
        ),
      ];
    }
    case "aroon": {
      const { up, down } = aroon(indicatorHigh, indicatorLow, config);

      const upSeriesData = up
        .map((value, idx) => ({ time: indicatorTimes[idx], value }))
        .filter((item) => !isNaN(item.value));
      const downSeriesData = down
        .map((value, idx) => ({ time: indicatorTimes[idx], value }))
        .filter((item) => !isNaN(item.value));

      if (!upSeriesData.length || !downSeriesData.length) return [];

      const currentPane = getNextEmptyPaneIndex();

      return [
        generateSeries(
          chartRef.current,
          downSeriesData,
          undefined,
          currentPane,
          0,
        ),
        generateSeries(
          chartRef.current,
          upSeriesData,
          undefined,
          currentPane,
          1,
        ),
      ];
    }
    case "atr": {
      const { atrLine } = atr(
        indicatorHigh,
        indicatorLow,
        indicatorClose,
        config,
      );
      const atrData = atrLine.map((value, idx) => ({
        time: indicatorTimes[idx],
        value,
      }));

      return [
        generateSeries(
          chartRef?.current,
          atrData,
          undefined,
          getNextEmptyPaneIndex(),
        ),
      ];
    }
    case "ao": {
      const aoData = ao(indicatorHigh, indicatorLow, { fast: 5, slow: 34 })
        .map((value, idx) => ({
          time: indicatorTimes[idx],
          value,
        }))
        .filter((item) => !isNaN(item.value));

      return [
        generateSeries(
          chartRef?.current,
          aoData,
          undefined,
          getNextEmptyPaneIndex(),
        ),
      ];
    }

    case "earnings": {
      if (!earningsData) return;

      let earningsSeriesData = [];
      earningsSeriesData = await earnings(
        tickerData,
        earningsData?.earnings_data,
      );

      const customSeriesView = new Earningsseries();

      const lineSeries = chartRef?.current?.addCustomSeries(customSeriesView, {
        priceLineVisible: true,
      });
      lineSeries.setData(earningsSeriesData);
      lineSeries.applyOptions({
        title: `${indicator?.misc_data?.pair_symbol} - Latest threshold ${earningsData?.realtime_data?.scaled_return?.toFixed(4)}`,
      });
      return [lineSeries];
    }
    case "bop": {
      const bopData = bop(
        indicatorOpen,
        indicatorHigh,
        indicatorLow,
        indicatorClose,
      )
        .map((value, idx) => ({
          time: indicatorTimes[idx],
          value,
        }))
        .filter((item) => !isNaN(item.value));

      return [
        generateSeries(
          chartRef?.current,
          bopData,
          undefined,
          getNextEmptyPaneIndex(),
        ),
      ];
    }
    case "bbands": {
      // Bollinger Bands
      const { upper, middle, lower } = bb(closePrices, config);
      const result = upper.map((high, i) => ({
        open: null,
        low: lower[i],
        close: middle[i],
        high,
        time: times[i],
      }));

      const customSeriesView = new HLCAreaSeries();
      if (lineSeriesRef?.length > 0) {
        lineSeriesRef[0].setData(result);
        lineSeriesRef[0].applyOptions({
          title: `B. Bands (Middle) - Period: ${config?.period}`,
        });
        return lineSeriesRef;
      }
      const color = getRandomColor();
      const lineSeries = chartRef?.current?.addCustomSeries(customSeriesView, {
        closeLineColor: color,
        color: color,
        priceLineVisible: false,
        title: `B. Bands (Middle) - Period: ${config?.period}`,
      });
      lineSeries.setData(result);

      return [lineSeries];
    }
    case "cmf": {
      const cmfData = cmf(
        indicatorHigh,
        indicatorLow,
        indicatorClose,
        indicatorVolume,
        config,
      )
        .map((value, idx) => ({
          time: indicatorTimes[idx],
          value,
        }))
        .filter((item) => !isNaN(item.value));

      return [
        generateSeries(
          chartRef?.current,
          cmfData,
          undefined,
          getNextEmptyPaneIndex(),
        ),
      ];
    }
    case "cfo": {
      const cfoData = cfo(indicatorClose)
        .map((value, idx) => ({
          time: indicatorTimes[idx],
          value,
        }))
        .filter((item) => !isNaN(item.value));

      return [
        generateSeries(
          chartRef?.current,
          cfoData,
          undefined,
          getNextEmptyPaneIndex(),
        ),
      ];
    }
    case "cci": {
      const cciData = cci(indicatorHigh, indicatorLow, indicatorClose, config)
        .map((value, idx) => ({
          time: indicatorTimes[idx],
          value,
        }))
        .filter((item) => !isNaN(item.value));

      return [
        generateSeries(
          chartRef?.current,
          cciData,
          undefined,
          getNextEmptyPaneIndex(),
        ),
      ];
    }
    case "donchian": {
      // Bollinger Bands
      const { upper, middle, lower } = dc(closePrices, config);
      const result = upper.map((high, i) => ({
        open: null,
        low: lower[i],
        close: middle[i],
        high,
        time: times[i],
      }));

      const customSeriesView = new HLCAreaSeries();
      if (lineSeriesRef?.length > 0) {
        lineSeriesRef[0].setData(result);
        lineSeriesRef[0].applyOptions({
          title: `Donchian - Period: ${config?.period}`,
        });
        return lineSeriesRef;
      }
      const color = getRandomColor();
      const lineSeries = chartRef?.current?.addCustomSeries(customSeriesView, {
        closeLineColor: color,
        color: color,
        priceLineVisible: false,
        title: `B. Bands (Middle) - Period: ${config?.period}`,
      });
      lineSeries.setData(result);

      return [lineSeries];
    }
    case "dema": {
      const demaData = generateLineData(dema(closePrices, config));
      return [
        addOrUpdateLineSeries(
          lineSeriesRef,
          indicator,
          chartRef,
          config,
          demaData,
        ),
      ];
    }

    case "ema": {
      const emaData = generateLineData(ema(closePrices, config));
      return [
        addOrUpdateLineSeries(
          lineSeriesRef,
          indicator,
          chartRef,
          config,
          emaData,
        ),
      ];
    }
    case "kdj": {
      return []; //skip for now not supported
    }
    case "kc": {
      // Keltner Channel
      const { upper, middle, lower } = kc(
        highPrices,
        lowPrices,
        closePrices,
        config,
      );
      const result = upper.map((high, i) => ({
        open: null,
        low: lower[i],
        close: middle[i],
        high,
        time: times[i],
      }));

      const customSeriesView = new HLCAreaSeries();
      if (lineSeriesRef?.length > 0) {
        lineSeriesRef[0].setData(result);
        lineSeriesRef[0].applyOptions({
          title: `B. Bands (Middle) - Period: ${config?.period}`,
        });
        return lineSeriesRef;
      }
      const color = getRandomColor();
      const lineSeries = chartRef?.current?.addCustomSeries(customSeriesView, {
        closeLineColor: color,
        color: color,
        priceLineVisible: false,
        title: `B. Bands (Middle) - Period: ${config?.period}`,
      });
      lineSeries.setData(result);

      return [lineSeries];
    }
    case "macd": {
      const { macdLine, signalLine } = macd(indicatorClose, {
        ...config,
        fast: config?.["short period"] ?? 12,
        slow: config?.["long period"] ?? 26,
        signal: config?.["signal period"] ?? 9,
      });

      const macdSeriesData = macdLine
        .map((value, idx) => ({ time: indicatorTimes[idx], value }))
        .filter((item) => !isNaN(item.value));
      const signalSeriesData = signalLine
        .map((value, idx) => ({ time: indicatorTimes[idx], value }))
        .filter((item) => !isNaN(item.value));

      if (!macdSeriesData.length || !signalSeriesData.length) return [];

      const currentPane = getNextEmptyPaneIndex();

      return [
        generateSeries(
          chartRef.current,
          macdSeriesData,
          `MACD - Fast: ${config?.["short period"]} - Slow: ${config?.["long period"]} `,
          currentPane,
          0,
        ),
        generateSeries(
          chartRef.current,
          signalSeriesData,
          `MACD Signal: ${config?.["signal period"]}`,
          currentPane,
          1,
        ),
      ];
    }
    case "mfi": {
      const mfiData = mfi(
        indicatorHigh,
        indicatorLow,
        indicatorClose,
        indicatorVolume,
        config,
      )
        .map((value, idx) => ({
          time: indicatorTimes[idx],
          value,
        }))
        .filter((item) => !isNaN(item.value));
      return [
        generateSeries(
          chartRef?.current,
          mfiData,
          undefined,
          getNextEmptyPaneIndex(),
        ),
      ];
    }
    case "nvi": {
      const nviData = nvi(indicatorClose, indicatorVolume)
        .map((value, idx) => ({
          time: indicatorTimes[idx],
          value,
        }))
        .filter((item) => !isNaN(item.value));
      return [
        generateSeries(
          chartRef?.current,
          nviData,
          undefined,
          getNextEmptyPaneIndex(),
        ),
      ];
    }
    case "massi": {
      const massiData = mi(indicatorHigh, indicatorLow, {
        ...config,
        emaPeriod: config?.["short period"] ?? 9,
        miPeriod: config?.["long period"] ?? 25,
      })
        .map((value, idx) => ({
          time: indicatorTimes[idx],
          value,
        }))
        .filter((item) => !isNaN(item.value));
      return [
        generateSeries(
          chartRef?.current,
          massiData,
          undefined,
          getNextEmptyPaneIndex(),
        ),
      ];
    }
    case "obv": {
      const obvData = obv(indicatorClose, indicatorVolume)
        .map((value, idx) => ({
          time: indicatorTimes[idx],
          value,
        }))
        .filter((item) => !isNaN(item.value));
      return [
        generateSeries(
          chartRef?.current,
          obvData,
          undefined,
          getNextEmptyPaneIndex(),
        ),
      ];
    }
    case "psar": {
      const { psarResult } = psar(highPrices, lowPrices, closePrices, config);
      const smaData = generateLineData(psarResult);
      return [
        addOrUpdateLineSeries(
          lineSeriesRef,
          indicator,
          chartRef,
          config,
          smaData,
        ),
      ];
    }
    case "ppo": {
      const { signal, ppoResult, histogram } = ppo(indicatorClose, {
        ...config,
        fast: 12,
        slow: 26,
        signal: 9,
      });
      const ppoData = ppoResult
        .map((value, idx) => ({
          time: indicatorTimes[idx],
          value,
        }))
        .filter((item) => !isNaN(item.value));
      const ppoSignalData = signal
        .map((value, idx) => ({
          time: indicatorTimes[idx],
          value,
        }))
        .filter((item) => !isNaN(item.value));
      const ppoHistogramData = histogram
        .map((value, idx) => ({
          time: indicatorTimes[idx],
          value,
          color: value >= 0 ? "#26a69a" : "#ef5350",
        }))
        .filter((item) => !isNaN(item.value));

      const paneIndex = getNextEmptyPaneIndex();

      return [
        generateSeries(chartRef?.current, ppoData, "PPO", paneIndex, 0),
        generateSeries(
          chartRef?.current,
          ppoSignalData,
          "Signal",
          paneIndex,
          1,
        ),
        generateSeries(
          chartRef?.current,
          ppoHistogramData,
          "Histogram",
          paneIndex,
          2,
          "histogram",
        ),
      ];
    }
    case "pvo": {
      const { signal, pvoResult, histogram } = pvo(volumePrices, {
        ...config,
        fast: config?.["short period"] ?? 12,
        slow: config?.["long period"] ?? 26,
        signal: config?.["signal period"] ?? 9,
      });
      const pvoData = pvoResult
        .map((value, idx) => ({
          time: indicatorTimes[idx],
          value,
        }))
        .filter((item) => !isNaN(item.value));
      const pvoSignalData = signal
        .map((value, idx) => ({
          time: indicatorTimes[idx],
          value,
        }))
        .filter((item) => !isNaN(item.value));
      const pvoHistogramData = histogram
        .map((value, idx) => ({
          time: indicatorTimes[idx],
          value,
          color: value >= 0 ? "#26a69a" : "#ef5350",
        }))
        .filter((item) => !isNaN(item.value));

      const paneIndex = getNextEmptyPaneIndex();

      return [
        generateSeries(chartRef?.current, pvoData, "PVO", paneIndex, 0),
        generateSeries(
          chartRef?.current,
          pvoSignalData,
          "Signal",
          paneIndex,
          1,
        ),
        generateSeries(
          chartRef?.current,
          pvoHistogramData,
          "Histogram",
          paneIndex,
          2,
          "histogram",
        ),
      ];
    }
    case "qstick": {
      const qstickData = qstick(indicatorOpen, indicatorClose, config)
        .map((value, idx) => ({
          time: indicatorTimes[idx],
          value,
        }))
        .filter((item) => !isNaN(item.value));

      return [
        generateSeries(
          chartRef?.current,
          qstickData,
          undefined,
          getNextEmptyPaneIndex(),
        ),
      ];
    }
    case "rsi": {
      const rsiData = rsi(indicatorOpen, config)
        .map((value, idx) => ({
          time: indicatorTimes[idx],
          value,
        }))
        .filter((item) => !isNaN(item.value));

      return [
        generateSeries(
          chartRef?.current,
          rsiData,
          undefined,
          getNextEmptyPaneIndex(),
        ),
      ];
    }
    case "sma": {
      const smaData = generateLineData(sma(closePrices, config));
      return [
        addOrUpdateLineSeries(
          lineSeriesRef,
          indicator,
          chartRef,
          config,
          smaData,
        ),
      ];
    }
    case "trima": {
      const tmaData = generateLineData(trima(closePrices, config));
      return [
        addOrUpdateLineSeries(
          lineSeriesRef,
          indicator,
          chartRef,
          config,
          tmaData,
        ),
      ];
    }
    case "stoch": {
      const { k, d } = stoch(indicatorHigh, indicatorLow, indicatorClose, {
        kPeriod: config?.K,
        dPeriod: config?.D,
      });
      const currentPane = getNextEmptyPaneIndex();

      const kSeriesData = k
        .map((value, idx) => ({ time: indicatorTimes[idx], value }))
        .filter((item) => !isNaN(item.value));
      const dSeriesData = d
        .map((value, idx) => ({ time: indicatorTimes[idx], value }))
        .filter((item) => !isNaN(item.value));

      if (!kSeriesData.length || !dSeriesData.length) return [];

      const dSeries = chartRef?.current?.addSeries(
        AreaSeries,
        {
          color: getRandomColor(),
          lineWidth: 2,
          priceLineVisible: true,
        },
        currentPane,
      );
      dSeries.setData(dSeriesData);

      const kSeries = chartRef?.current?.addSeries(
        AreaSeries,
        {
          color: getRandomColor(),
          lineWidth: 2,
          priceLineVisible: true,
        },
        currentPane,
      );
      kSeries.setData(kSeriesData);

      return [dSeries, kSeries];
    }
    case "tema": {
      const temaData = generateLineData(tema(closePrices, config));
      return [
        addOrUpdateLineSeries(
          lineSeriesRef,
          indicator,
          chartRef,
          config,
          temaData,
        ),
      ];
    }
    case "true_range": {
      const trueRangeData = trueRange(
        indicatorHigh,
        indicatorLow,
        indicatorClose,
      )
        .map((value, idx) => ({
          time: indicatorTimes[idx],
          value,
        }))
        .filter((item) => !isNaN(item.value));

      return [
        generateSeries(
          chartRef?.current,
          trueRangeData,
          undefined,
          getNextEmptyPaneIndex(),
        ),
      ];
    }
    case "hl2": {
      const typpriceData = generateLineData(
        typprice(highPrices, lowPrices, closePrices),
      );
      return [
        addOrUpdateLineSeries(
          lineSeriesRef,
          indicator,
          chartRef,
          config,
          typpriceData,
        ),
      ];
    }
    case "ui": {
      return []; // TODO: Implement UI
    }
    case "vwap": {
      const vwapData = generateLineData(
        vwap(closePrices, volumePrices, config),
      );
      return [
        addOrUpdateLineSeries(
          lineSeriesRef,
          indicator,
          chartRef,
          config,
          vwapData,
        ),
      ];
    }
    case "vwma": {
      const vwmaData = generateLineData(
        vwma(closePrices, volumePrices, config),
      );
      return [
        addOrUpdateLineSeries(
          lineSeriesRef,
          indicator,
          chartRef,
          config,
          vwmaData,
        ),
      ];
    }
    case "vortex": {
      const { plus, minus } = vortex(
        indicatorHigh,
        indicatorLow,
        indicatorClose,
        config,
      );
      const plusData = plus
        .map((value, idx) => ({
          time: indicatorTimes[idx],
          value,
        }))
        .filter((item) => !isNaN(item.value) && !isNaN(item.time));
      const minusData = minus
        .map((value, idx) => ({
          time: indicatorTimes[idx],
          value,
        }))
        .filter((item) => !isNaN(item.value) && !isNaN(item.time));
      const currentPane = getNextEmptyPaneIndex();

      return [
        generateSeries(chartRef.current, plusData, undefined, currentPane, 0),
        generateSeries(chartRef.current, minusData, undefined, currentPane, 1),
      ];
    }
    case "willr": {
      const willrResult = willr(
        indicatorHigh,
        indicatorLow,
        indicatorClose,
        config,
      );
      const willrData = willrResult
        .map((value, idx) => ({
          time: indicatorTimes[idx],
          value,
        }))
        .filter((item) => !isNaN(item.value));

      return [
        generateSeries(
          chartRef?.current,
          willrData,
          undefined,
          getNextEmptyPaneIndex(),
        ),
      ];
    }
    case "bias":
    case "cg":
    case "ebsw":
    case "er":
    case "cti":
    case "cmo":
    case "rsx":
    case "stc":
    case "smi":
    case "td_seq":
    case "coppock":
    case "pvol":
    case "pvr":
    case "pvt":
    case "pvi":
    case "adosc":
    case "natr":
    case "pdist":
    case "rvi":
    case "thermo":
    case "hwc":
    case "aberration":
    case "adx":
    case "dm":
    case "fisher":
    case "inertia":
    case "slope":
    case "squeeze":
    case "squeeze_pro":
    case "trix":
    case "tsi":
    case "uo":
    case "mom":
    case "pgo":
    case "qqe":
    case "roc":
    case "hilo":
    case "entopy":
    case "kurtosis":
    case "mad":
    case "decay":
    case "vhf":
    case "decreasing":
    case "increasing":
    case "dpo":
    case "skew":
    case "stdev":
    case "tos_stdevall":
    case "variance":
    case "chop":
    case "zscore":
    case "eri":
    case "efi":
    case "eom":
    case "rvol":
    case "alma":
    case "fwma":
    case "hma":
    case "hwma":
    case "jma":
    case "kama":
    case "linreg":
    case "mgcd":
    case "midpoint":
    case "midprice":
    case "rma":
    case "sinwma":
    case "vidya":
    case "zlma":
    case "amat":
    case "hlc3":
    case "ohlc4":
    case "pwma":
    case "ssf":
    case "swma":
    case "t3":
    case "wcp":
    case "wma":
    case "median":
    case "quantile":
    case "ttm_trend":
    case "tsignals":
    case "supertend":
    case "stochrsi":
    case "cksp":
    case "rvgi": {
      const { result: chartData, containsInnerObject } =
        await getCommonIndicatorData(indicator, config);

      const isSubPane = ![
        "alma",
        "fwma",
        "hma",
        "hwma",
        "jma",
        "kama",
        "linreg",
        "mgcd",
        "midpoint",
        "midprice",
        "rma",
        "sinwma",
        "vidya",
        "zlma",
        "amat",
        "hlc3",
        "ohlc4",
        "pwma",
        "ssf",
        "swma",
        "t3",
        "wcp",
        "wma",
        "median",
        "quantile",
        "ttm_trend",
        "tsignals",
        "supertend",
        "cksp",
      ].includes(indicator.short_name);

      const currentPane = isSubPane ? getNextEmptyPaneIndex() : undefined;

      if (containsInnerObject) {
        const keys = Object.keys(chartData[0].value);

        return keys.map((key, index) => {
          const seriesData = chartData.map((item: any) => ({
            time: item.time,
            value: item.value[key],
          }));

          return generateSeries(
            chartRef?.current,
            seriesData,
            key,
            currentPane,
            index,
          );
        });
      }

      return [
        generateSeries(chartRef?.current, chartData, undefined, currentPane),
      ];
    }
    case "ichimoku": {
      const chartData = await getCommonIndicatorData(
        indicator,
        { period: 20 },
        true,
      );
      const {
        laggingSpan,
        baseLine,
        leadingSpanA,
        leadingSpanB,
        conversionLine,
      } = await ichimoku(chartData);
      const newTime = indicatorTimes?.slice(-1000);

      const band = leadingSpanA
        .map((high: number, i: number) => ({
          open: null,
          close: (Number(high) + leadingSpanB[i]) / 2,
          high,
          low: leadingSpanB[i],
          time: newTime?.[i],
        }))
        .filter((i: any) => i.time && i.high && i.low);

      const _laggingSpan = laggingSpan
        .map((value: any, index: number) => ({
          time: newTime?.[index],
          value,
        }))
        .filter((i: any) => i.time && Boolean(i.value));
      const _kijun = baseLine
        .map((value: any, index: number) => ({
          time: newTime?.[index],
          value,
        }))
        .filter((i: any) => i.time && Boolean(i.value));
      const _tenkan = conversionLine
        .map((value: any, index: number) => ({
          time: newTime?.[index],
          value,
        }))
        .filter((i: any) => i.time && Boolean(i.value));

      const customSeriesView = new HLCAreaSeries();
      if (
        lineSeriesRef?.[0] &&
        lineSeriesRef?.[1] &&
        lineSeriesRef?.[2] &&
        lineSeriesRef?.[3]
      ) {
        lineSeriesRef[0].setData(band);
        lineSeriesRef[1].setData(_laggingSpan);
        lineSeriesRef[2].setData(_kijun);
        lineSeriesRef[3].setData(_tenkan);
        return lineSeriesRef;
      }
      const color = getRandomColor();
      const bandSeries = chartRef?.current?.addCustomSeries(customSeriesView, {
        closeLineColor: "transparent",
        priceLineVisible: false,
        highLineColor: color,
        lowLineColor: color,
        areaTopColor: color,
        areaBottomColor: color,
        title: `SPANA - SPANB`,
      });
      bandSeries.setData(band);

      return [
        bandSeries,
        generateSeries(
          chartRef?.current,
          _laggingSpan,
          undefined,
          undefined,
          1,
        ),
        generateSeries(chartRef?.current, _kijun, undefined, undefined, 2),
        generateSeries(chartRef?.current, _tenkan, undefined, undefined, 3),
      ];
    }
    case "grid": {
      let buyLevels, sellLevels, basePrice;
      if (!gridData) {
        const gridStep = config?.["grid step percentage"];
        const highLowPercent = config?.["high low percentage"];
        const price = currentPrice?.toString() ?? "0";

        const gridData = await getGrideValues(gridStep, price, highLowPercent);

        if (
          !Array.isArray(gridData) ||
          gridData.length < 2 ||
          !Array.isArray(gridData[0]) ||
          !Array.isArray(gridData[1])
        ) {
          return;
        }

        buyLevels = gridData?.[0];
        sellLevels = gridData?.[1];
        basePrice = currentPrice;
      } else {
        // Extract buy and sell levels from the result
        buyLevels = gridData?.buy_levels;
        sellLevels = gridData?.sell_levels;
      }

      const basePriceLine = addGrid(
        lineSeriesRef,
        indicator,
        chartRef,
        config,
        candleStickSeriesRef,
        buyLevels,
        sellLevels,
        basePrice,
      );
      return basePriceLine;
    }
    default:
      return [];
  }
};

const addOrUpdateLineSeries = (
  lineSeriesRef: any,
  indicator: any,
  chartRef: any,
  config: any,
  data: any,
  index?: number,
  color?: string,
  forcetitle?: string,
) => {
  const title =
    forcetitle ??
    `${indicator?.short_name?.toUpperCase()}${
      config.period ? `- Period: ${config.period}` : ""
    }`;

  if (!lineSeriesRef?.[index ?? 0]) {
    const series = chartRef?.current?.addSeries(LineSeries, {
      color: color ?? getRandomColor(),
      lineWidth: 2,
      priceLineVisible: true,
      title,
    });
    series?.setData(data);
    return series;
  } else {
    lineSeriesRef?.[index ?? 0]?.setData(data);
    lineSeriesRef?.[index ?? 0]?.applyOptions({ title });
    return lineSeriesRef?.[index ?? 0];
  }
};

const addOrUpdatePriceLineSeries = (
  lineSeriesRef: any,
  indicator: any,
  chartRef: any,
  config: any,
  price: any,
  index?: number,
  color?: string,
  forcedTitle?: string,
) => {
  const title =
    forcedTitle ??
    `${indicator?.short_name?.toUpperCase()}${
      config?.period ? `- Period: ${config.period}` : ""
    }`;

  if (!lineSeriesRef?.[index ?? 0]) {
    const series = chartRef?.current?.createPriceLine({
      price: price,
      color: color,
      lineWidth: 2,
      lineStyle: LineStyle.Dotted,
      axisLabelVisible: true,
      title: title,
    });
    return series;
  } else {
    lineSeriesRef[index ?? 0].applyOptions({ price, title, color });
    return lineSeriesRef[index ?? 0];
  }
};

export const addGrid = (
  lineSeriesRef: any,
  indicator: any,
  chartRef: any,
  config: any,
  candleStickSeriesRef: any,
  buyLevels: any[],
  sellLevels: any[],
  base_price: any,
) => {
  // Get the visible time range of the chart
  const timeScale = chartRef?.current?.timeScale();
  const visibleRange = timeScale.getVisibleRange();

  const lines = [];
  buyLevels?.forEach((level: number, index: number) => {
    const line = addOrUpdatePriceLineSeries(
      lineSeriesRef,
      indicator,
      candleStickSeriesRef,
      config,
      level,
      index,
      "green",
    );
    if (line) lines.push(line);
  });
  sellLevels?.forEach((level: number, index: number) => {
    const line = addOrUpdatePriceLineSeries(
      lineSeriesRef,
      indicator,
      candleStickSeriesRef,
      config,
      level,
      buyLevels?.length + index,
      "red",
    );
    if (line) lines.push(line);
  });

  const basePriceLine = addOrUpdatePriceLineSeries(
    lineSeriesRef,
    indicator,
    candleStickSeriesRef,
    config,
    base_price ?? 0,
    buyLevels?.length + sellLevels?.length + 1,
    "blue",
    "Base Price",
  );
  if (basePriceLine) lines.push(basePriceLine);

  return lines;
};

export const prepareIndicatorConfig = (indicator: any) => {
  const config: Record<string, any> = {};

  indicator?.other_inputs?.forEach?.((param: any) => {
    if (["input", "dropdown"].includes(param.type)) {
      let value = param.value ?? param.min_value;

      if (param.name === "period") {
        value = Number(value);
      }

      config[param.name] = value;
    }
  });

  return config;
};

const getRandomColor = () => {
  const hue = Math.floor(Math.random() * 360);
  const saturation = Math.floor(Math.random() * 30) + 70; // 70% to 100%
  const lightness = Math.floor(Math.random() * 20) + 70; // 70% to 90%
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

export const resizeMainChart = (
  chartRef: any,
  mainChartRef: any,
  bottomChartRefs: any,
) => {
  if (!chartRef.current || !mainChartRef?.current) return;

  const div = mainChartRef.current;
  const countWithIds = Object.values(bottomChartRefs).filter(
    (chart: any) =>
      chart?.id?.current !== null && chart?.id?.current !== undefined,
  ).length;
  chartRef.current.resize(
    div.clientWidth || 0,
    Math.max((div.clientHeight || 0) - countWithIds * 128, 0),
  );
};

type AreaSeriesData = {
  time: number;
  value: number;
};

function filterValidAreaSeriesData(data: AreaSeriesData[]): AreaSeriesData[] {
  const MIN_VALUE = -90071992547409.91;
  const MAX_VALUE = 90071992547409.91;

  return data.filter(
    ({ value }) =>
      typeof value === "number" &&
      !isNaN(value) &&
      isFinite(value) &&
      value >= MIN_VALUE &&
      value <= MAX_VALUE,
  );
}
